<div class="col-12 col-sm-11 col-md-11 col-lg-9 col-xl-9 col-xxl-9">
    <c11n-modal-dialog
        #managedAccountModal
        (primaryClick)="onManagedAccountModalContinue()"
        (secondaryClick)="onManagedAccountModalBack()"
        [headingText]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnSelectAccessOnlineTrasactionsNotAvailable?.heading"
        [maxWidth]="660"
        [primaryButtonLabel]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnSelectAccessOnlineTrasactionsNotAvailable?.navContinue"
        [secondaryButtonLabel]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnSelectAccessOnlineTrasactionsNotAvailable?.navBack"
        [useContentWidth]="false"
        [variant]="'warning'">
        <div c11n-modal-dialog-body>
            <!-- TODO: Remove "Note:" from text in AEM -->
            <div
                    [innerHTML]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnSelectAccessOnlineTrasactionsNotAvailable?.text1 | replaceParam: 'accessLevelAccess':((accessLevelLabel$ | async) | lowercase)"
                    class="c11n-text-md c11n-space-stack-2x"
                    data-cy="managedAccountModal">
            </div>
        </div>
    </c11n-modal-dialog>
    <c11n-modal-dialog
        #cashManagementModal
        (primaryClick)="onCashManagementModalContinue()"
        (secondaryClick)="onCashManagementModalBack()"
        [headingText]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnInfoOnlyAccNumberVisible?.heading"
        [maxWidth]="660"
        [primaryButtonLabel]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnInfoOnlyAccNumberVisible?.navContinue"
        [secondaryButtonLabel]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnInfoOnlyAccNumberVisible?.navBack"
        [useContentWidth]="false"
        [variant]="'warning'">
        <div c11n-modal-dialog-body>
            <div
                    [innerHTML]="grantorContent?.add_user?.frmcSelectAccessLevel?.wrnInfoOnlyAccNumberVisible?.text1"
                    class="c11n-text-md c11n-space-stack-2x"
                    data-cy="cashManagementModal">
            </div>
        </div>
    </c11n-modal-dialog>
    <h1 class="c11n-text-xl-headline c11n-space-stack-6x">
        Choose the accounts to give
        @if (granteeName$ | async; as granteeName) {
            {{ granteeName }}
        }
        @if (accessLevelLabel$ | async; as accessLevel) {
            {{ accessLevel | lowercase }}
        }
        to
    </h1>

    @if (isLoading) {
        <span class="visually-hidden" role="status">Loading accounts...</span>
        <div aria-hidden="true" class="skeleton-container" data-cy="accounts-skeleton-loader">
            <div class="c11n-space-stack-2x">
                <c11n-skeleton-loader
                    variant="textBlock"
                    textHeight="sm"
                    [lineCount]="1">
                </c11n-skeleton-loader>
            </div>
            @for (i of [1, 2, 3]; track i) {
                <div class="skeleton-account-row">
                    <c11n-skeleton-loader
                        variant="shape"
                        shapeWidth="24px"
                        shapeHeight="24px">
                    </c11n-skeleton-loader>
                    <c11n-skeleton-loader
                        variant="textBlock"
                        textHeight="md"
                        [lineCount]="1"
                        class="skeleton-account-name">
                    </c11n-skeleton-loader>
                </div>
            }
        </div>
    } @else {
        @if (accountGroups$ | async; as groups) {
            <!-- No Eligible Accounts Error Banner (only show when other groups have items) -->
            @if (groups.eligible.length === 0 && (groups.pendingRequests.length > 0 || groups.requireForm.length > 0 || groups.otherIneligible.length > 0 || groups.temporarilyIneligible.length > 0)) {
                <c11n-banner
                        [hasCloseControl]="false"
                        [size]="'small'"
                        [variant]="'error'"
                        class="c11n-space-stack-4x"
                        data-cy="no-eligible-accounts-banner"
                        role="alert">
                    <div class="c11n-text-md--crop">
                        You have no eligible accounts to select.
                    </div>
                </c11n-banner>
            }

            <!-- Select All / Deselect All Link -->
            @if (groups.eligible.length > 0) {
                <div>
                    <a
                            (click)="toggleSelectAll()"
                            (keydown.enter)="toggleSelectAll()"
                            class="select-all-link"
                            data-cy="select-all-link"
                            tabindex="0">
                        {{ allAccountsSelected ? 'Deselect all' : 'Select all' }}
                    </a>
                </div>
            }

            <!-- Eligible Accounts -->
            @if (groups.eligible.length > 0) {
                <div class="c11n-space-stack-4x">
                    @for (account of groups.eligible; track account.accountId) {
                        <div class="eligible-account">
                            <c11n-checkbox
                                    (stateChange)="onAccountChange($event, account)"
                                    [attr.data-cy]="'account-checkbox-' + account.accountId"
                                    [labelText]="account.accountName">
                                <input
                                        [checked]="isAccountSelected(account)"
                                        [name]="'account-' + account.accountId"
                                        [value]="account.accountId"
                                        c11nCheckboxInput
                                        type="checkbox"/>
                            </c11n-checkbox>
                        </div>
                    }
                    <!-- Selection Error -->
                    @if (showSelectionError) {
                        <div #selectionError class="selection-error c11n-space-stack-3x" data-cy="selection-error"
                             role="alert">
                            <c11n-icon [name]="'error'" [size]="'small'"></c11n-icon>
                            <strong>Error:</strong> Please choose one or more accounts from the list.
                        </div>
                    }
                </div>
            }

            <!-- Accounts with Pending Requests -->
            @if (groups.pendingRequests.length > 0) {
                <div class="c11n-space-stack-4x">
                    <div class="section-header">
                        Accounts that have pending requests:
                    </div>
                    @for (pendingInfo of groups.pendingRequests; track pendingInfo.account.accountId) {
                        <div class="c11n-space-stack-x">
                            <span class="section-account-name">
                                {{ pendingInfo.account.accountName }}
                                ({{ getAccessLevelForPendingAccount(pendingInfo) }})
                            </span>
                        </div>
                    }
                </div>
            }

            <!-- Accounts that Require a Form -->
            @if (groups.requireForm.length > 0) {
                <div class="c11n-space-stack-4x">
                    <div class="section-header">
                        Accounts that require a form:
                    </div>
                    @for (account of groups.requireForm; track account.accountId) {
                        <div class="c11n-space-stack-x">
                            <span class="section-account-name">{{ account.accountName }}</span>
                        </div>
                    }
                    <div class="c11n-space-stack-2x">
                        <a
                                (click)="navigateToForm()"
                                c11n-link
                                class="form-link"
                                data-cy="form-link"
                                variant="primary-reinforced">
                            Take me to the form.
                        </a>
                    </div>
                </div>
            }

            <!-- Other Ineligible Accounts -->
            @if (groups.otherIneligible.length > 0) {
                <div class="c11n-space-stack-4x">
                    <div class="section-header">
                        Other ineligible accounts:
                    </div>
                    @for (account of groups.otherIneligible; track account.accountId) {
                        <div class="c11n-space-stack-x">
                            <span class="section-account-name">
                                {{ account.accountName }}{{ getIneligibleReasonLabel(account) }}
                            </span>
                        </div>
                    }
                </div>
            }

            <!-- Temporarily Ineligible Accounts -->
            @if (groups.temporarilyIneligible.length > 0) {
                <div class="c11n-space-stack-4x">
                    <div class="section-header">
                        Temporarily ineligible accounts:
                    </div>
                    @for (account of groups.temporarilyIneligible; track account.accountId) {
                        <div class="c11n-space-stack-x">
                            <span class="section-account-name">
                                {{ account.accountName }}
                            </span>
                        </div>
                    }
                </div>
            }

            <!-- No Accounts Available Message -->
            @if (groups.eligible.length === 0 && groups.pendingRequests.length === 0 && groups.requireForm.length === 0 && groups.otherIneligible.length === 0 && groups.temporarilyIneligible.length === 0) {
                <c11n-banner
                        [hasCloseControl]="false"
                        [size]="'small'"
                        [variant]="'error'"
                        class="c11n-space-stack-4x"
                        data-cy="no-accounts-available-banner"
                        role="alert">
                    <div class="c11n-text-md--crop">
                        No accounts available.
                    </div>
                </c11n-banner>
            }
        }
    }
</div>
